import React, { useRef } from 'react';
import styled from '@emotion/styled';
import { Icon, Flex, Box, Button } from '@opendoor/bricks/core';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import { EventCard, EventCardProps } from '@opendoor/cinderblocks/platform/EventCard';

import { ScheduledEventCard, IScheduleCardProps } from 'shared/components/overview/ScheduledEvent';

export interface EventListProps {
  /** scheduledEvents are upcoming events with timestamp */
  scheduledEvents: Array<IScheduleCardProps>;
  /** otherEvents are other general events, those are displayed after scheduledEvents in the list */
  otherEvents: Array<EventCardProps>;
}

const HideScrollbar = styled(Flex)`
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  overflow-x: scroll;
  scroll-behavior: smooth;

  ::-webkit-scrollbar {
    display: none;
  }
`;

// component-test-check-ignore
/** TODO: Add documentation */
export const EventList: React.FC<EventListProps> = ({ scheduledEvents, otherEvents }) => {
  const numEvents = scheduledEvents.length + otherEvents.length;
  const { isMobile } = useDevice();

  const [shouldShowRightScrollable, setShouldShowRightScrollable] = React.useState<boolean>(
    numEvents >= 3,
  );
  const [shouldShowLeftScrollable, setShouldShowLeftScrollable] = React.useState<boolean>(false);

  const scrollableElem = useRef<HTMLDivElement | null>(null);

  /** TODO: Add documentation */
  const onContainerScroll = (e: React.UIEvent<HTMLElement>) => {
    if (numEvents < 2) {
      return;
    }
    const element = e.currentTarget;
    const currentScrollWidth = element.scrollLeft + element.offsetWidth;
    if (element.scrollLeft > 0 && !shouldShowLeftScrollable) {
      setShouldShowLeftScrollable(true);
    } else if (element.scrollLeft <= 0 && shouldShowLeftScrollable) {
      setShouldShowLeftScrollable(false);
    }
    if (currentScrollWidth >= element.scrollWidth && shouldShowRightScrollable) {
      setShouldShowRightScrollable(false);
    } else if (currentScrollWidth < element.scrollWidth && !shouldShowRightScrollable) {
      setShouldShowRightScrollable(true);
    }
  };

  /** TODO: Add documentation */
  const handleScroll = (side: 'left' | 'right') => {
    const targetElem = scrollableElem.current;
    if (!targetElem) return;

    if (side === 'left') {
      targetElem.scrollLeft -= 400;
    } else {
      targetElem.scrollLeft += 400;
    }
  };

  /** TODO: Add documentation */
  const getWidth = (numEvents: number) => {
    if (isMobile) {
      return numEvents > 1 ? '80%' : '100%';
    }

    switch (numEvents) {
      case 1:
        return '100%';
      case 2:
        return '49.5%';
      default:
        return '49%';
    }
  };

  /** TODO: Add documentation */
  const scrollableBox = (side: 'left' | 'right') => (
    <Flex
      position="absolute"
      left={side === 'left' ? 0 : undefined}
      right={side === 'right' ? 0 : undefined}
      top={0}
      height="100%"
      width="80px"
      background={`linear-gradient(${
        side === 'right' ? '270deg' : '90deg'
      }, #FFFFFF 29.07%, rgba(255, 255, 255, 0) 100%);`}
      alignItems="center"
    >
      <Button
        aria-label={`${side === 'left' ? 'Previous' : 'Next'} event`}
        analyticsName={`scheduled-events.scroll.${side}`}
        marginLeft={side === 'left' ? 0 : 'auto'}
        marginRight={side === 'right' ? 0 : 'auto'}
        onClick={() => handleScroll(side)}
        variant="icon"
      >
        <Icon size={16} name={side === 'left' ? 'chevron-left' : 'chevron-right'} />
      </Button>
    </Flex>
  );
  return (
    <Box position="relative">
      {!isMobile && shouldShowLeftScrollable && scrollableBox('left')}
      <HideScrollbar onScroll={onContainerScroll} ref={scrollableElem}>
        {scheduledEvents.map((event: IScheduleCardProps, index) => (
          <Box
            width={getWidth(numEvents)}
            key={index}
            mr={index === numEvents - 1 && otherEvents.length === 0 ? 0 : 3}
            flexShrink={0}
          >
            <ScheduledEventCard {...event} />
          </Box>
        ))}
        {otherEvents.map((event, index) => (
          <Box
            width={getWidth(numEvents)}
            key={index}
            mr={index === numEvents - 1 ? 0 : 3}
            flexShrink={0}
          >
            <EventCard {...event} />
          </Box>
        ))}
      </HideScrollbar>
      {!isMobile && shouldShowRightScrollable && scrollableBox('right')}
    </Box>
  );
};
